import { useLayoutEffect, useMemo, useRef } from "react";
import {
  IconOrderPending,
  IconOrderProcessing,
  IconOrderShipped,
  IconOrderDelivered,
  IconOrderCanceled,
} from "../../../icons/Icons";

const ESTADO_LABELS = {
  0: "Pendiente",
  1: "Procesando",
  2: "Enviado",
  3: "Entregado",
  4: "Cancelado",
};

const ESTADO_TONE = {
  Pendiente: "pending",
  Procesando: "processing",
  Enviado: "sent",
  Entregado: "delivered",
  Cancelado: "canceled",
  "Pedido creado": "created",
};

const ESTADO_ICONS = {
  pending: IconOrderPending,
  processing: IconOrderProcessing,
  sent: IconOrderShipped,
  delivered: IconOrderDelivered,
  canceled: IconOrderCanceled,
  created: IconOrderPending,
};

const toTime = (v) => {
  const t = new Date(v || 0).getTime();
  return Number.isFinite(t) ? t : 0;
};

const formatDateTime = (dt) => {
  if (!dt) return "—";
  const d = new Date(dt);
  if (Number.isNaN(d.getTime())) {
    const raw = String(dt).split("T")[0];
    return raw ? raw.split("-").reverse().join("/") : "—";
  }
  const dd = String(d.getDate()).padStart(2, "0");
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const yyyy = d.getFullYear();
  const hh = String(d.getHours()).padStart(2, "0");
  const mi = String(d.getMinutes()).padStart(2, "0");
  return `${dd}/${mm}/${yyyy} · ${hh}:${mi}`;
};

function estadoLabel(id) {
  const n = Number(id);
  if (Number.isFinite(n) && ESTADO_LABELS[n]) return ESTADO_LABELS[n];
  return id == null || id === "" ? "—" : String(id);
}

function userLabel(row) {
  const name = `${row?.nombre || ""} ${row?.apellido || ""}`.trim();
  if (name) return name;
  if (row?.usuario) return `@${row.usuario}`;
  return "Sistema";
}

function toneForEvent(ev) {
  if (ev.type === "created") return "created";
  return ESTADO_TONE[ev.to] || "pending";
}

function buildEvents(historial, pedido) {
  const rows = Array.isArray(historial) ? historial : [];
  const events = [];

  if (pedido?.fecha_creado) {
    events.push({
      id: "created",
      type: "created",
      fecha: pedido.fecha_creado,
      from: null,
      to: "Pedido creado",
      user: "Sistema",
      comentario: null,
    });
  }

  for (const row of rows) {
    events.push({
      id: row.pedido_estado_historial_id,
      type: "change",
      fecha: row.fecha_creado || row.fecha,
      from: estadoLabel(row.estado_anterior),
      to: estadoLabel(row.estado_nuevo),
      user: userLabel(row),
      comentario: row.comentario || null,
    });
  }

  return events.sort((a, b) => toTime(a.fecha) - toTime(b.fecha));
}

function TimelineIcon({ tone }) {
  const Icon = ESTADO_ICONS[tone] || IconOrderPending;
  return (
    <span className={`ovm-timeline__icon ovm-timeline__icon--${tone}`}>
      <Icon />
    </span>
  );
}

function OrderDetailTimeline({ historial, pedido, loading }) {
  const scrollRef = useRef(null);
  const events = useMemo(
    () => buildEvents(historial, pedido),
    [historial, pedido]
  );

  useLayoutEffect(() => {
    const el = scrollRef.current;
    if (!el || loading) return;
    el.scrollTop = el.scrollHeight;
  }, [events, loading]);

  return (
    <aside className="ovm-timeline" aria-label="Historial del pedido">
      <div className="ovm-timeline__header">
        <h3 className="ovm-timeline__title">Historial</h3>
        <p className="ovm-timeline__subtitle">
          {loading
            ? "Cargando eventos..."
            : "Desde la creación del pedido hacia el estado actual"}
        </p>
        <span className="ovm-timeline__count">
          {loading ? "…" : `${events.length} evento${events.length === 1 ? "" : "s"}`}
        </span>
      </div>

      <div className="ovm-timeline__scroll" ref={scrollRef}>
        {loading ? (
          <div className="ovm-timeline__loading">
            <div className="ovm-skeleton ovm-skeleton--timeline" />
            <div className="ovm-skeleton ovm-skeleton--timeline" />
            <div className="ovm-skeleton ovm-skeleton--timeline" />
          </div>
        ) : events.length === 0 ? (
          <div className="ovm-timeline__empty">
            <div className="ovm-timeline__empty-icon">◎</div>
            <p>Aún no hay cambios registrados en este pedido.</p>
          </div>
        ) : (
          <div className="ovm-timeline__track">
            <div className="ovm-timeline__spine" aria-hidden="true" />

            <div className="ovm-timeline__list">
              {events.map((ev, idx) => {
                const isOrigin = idx === 0;
                const isLatest = idx === events.length - 1;
                const tone = toneForEvent(ev);

                return (
                  <article
                    key={ev.id}
                    className={[
                      "ovm-timeline__item",
                      `is-${tone}`,
                      isOrigin ? "is-origin" : "",
                      isLatest ? "is-latest" : "",
                    ]
                      .filter(Boolean)
                      .join(" ")}
                  >
                    <div className="ovm-timeline__node">
                      <TimelineIcon tone={tone} />
                    </div>

                    <div className="ovm-timeline__card">
                      {isOrigin ? (
                        <span className="ovm-timeline__tag is-origin">Inicio</span>
                      ) : null}
                      {isLatest && !isOrigin ? (
                        <span className="ovm-timeline__tag is-current">Actual</span>
                      ) : null}

                      <time className="ovm-timeline__when" dateTime={ev.fecha}>
                        {formatDateTime(ev.fecha)}
                      </time>

                      {ev.type === "created" ? (
                        <h4 className="ovm-timeline__headline">{ev.to}</h4>
                      ) : (
                        <div className="ovm-timeline__transition">
                          <span className="ovm-timeline__pill">{ev.from}</span>
                          <span className="ovm-timeline__arrow" aria-hidden="true">
                            →
                          </span>
                          <span className="ovm-timeline__pill is-next">{ev.to}</span>
                        </div>
                      )}

                      <div className="ovm-timeline__user">
                        <span className="ovm-timeline__user-label">Por</span>
                        <span>{ev.user}</span>
                      </div>

                      {ev.comentario ? (
                        <p className="ovm-timeline__comment">{ev.comentario}</p>
                      ) : null}
                    </div>
                  </article>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </aside>
  );
}

export default OrderDetailTimeline;
